import {
  QueryClient,
  type MutationCacheNotifyEvent,
  type QueryCacheNotifyEvent,
} from "@tanstack/react-query";
import * as Sentry from "@sentry/nextjs";
import {
  defaultShouldReportMutationError,
  getFirstGraphQlErrorFromAction,
  getGraphQlErrorFromUnknown,
  graphQlOperationName,
  graphQlPathTag,
  graphQlSentryErrorCode,
  graphQlSentryStatusCode,
  resolveGraphQlSentryStack,
  shouldCaptureRest400QueryError,
  shouldIgnoreGraphQlQueryMessage,
  toSafeReportedError,
  toSafeStack,
  toSentryErrorMessage,
  type ErrorWithStatusCode,
} from "./utils";

type CaptureException = (
  exception: unknown,
  captureContext?: Parameters<typeof Sentry.captureException>[1],
) => ReturnType<typeof Sentry.captureException>;

type QueryClientConfig = NonNullable<
  ConstructorParameters<typeof QueryClient>[0]
>;

export type CreateQueryClientWithSentryOptions = {
  /** Same shape as QueryClient defaultOptions (e.g. queries.retry). */
  defaultOptions?: QueryClientConfig["defaultOptions"];
  /** Override Sentry capture (e.g. for tests or custom reporting). */
  captureException?: CaptureException;
  /** Override which mutation errors are reported (default: 5xx, network, 400/422). */
  shouldReportMutationError?: (error: unknown) => boolean;
  /** GraphQL error messages (exact match after trim) not reported from the query cache. */
  ignoredGraphQlQueryMessages?: readonly string[];
};

/**
 * Creates a QueryClient with default options and attaches Sentry reporting for
 * query and mutation cache errors. Use this in each app's lib/query-client.ts (or equivalent).
 *
 * Behavior (can be overridden via options):
 * - Query cache: reports REST 400 and GraphQL errors from 200 responses.
 * - Mutation cache: reports 5xx, network failures, and 4xx 400/422 (controlled flows).
 */
export function createQueryClientWithSentry(
  options: CreateQueryClientWithSentryOptions = {},
): QueryClient {
  const {
    defaultOptions = {},
    captureException: capture = Sentry.captureException.bind(Sentry),
    shouldReportMutationError = defaultShouldReportMutationError,
    ignoredGraphQlQueryMessages,
  } = options;

  const ignoredGraphQlQueryMessagesSet = new Set<string>(
    (ignoredGraphQlQueryMessages ?? []).map((m) => m.trim()).filter(Boolean),
  );

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        ...defaultOptions?.queries,
      },
      ...defaultOptions,
    },
  });

  // Query cache: REST 400 and GraphQL errors
  queryClient.getQueryCache().subscribe((event: QueryCacheNotifyEvent) => {
    if (event?.type === "observerResultsUpdated" || event?.type === "updated") {
      const error = event?.query?.state?.error;
      if (error && shouldCaptureRest400QueryError(error)) {
        const statusCode = (
          error as { response?: { status?: number } }
        )?.response?.status?.toString();
        // Not `${error}` / the raw error: a graphql-request ClientError's
        // own `.message` embeds the full request, variables included (see
        // the mutation-cache subscriber below).
        const safeError = toSafeReportedError(error);
        const sentryError = new Error(
          `TanStack Query Error: ${safeError.name}: ${toSentryErrorMessage(safeError.message, "Error")}`,
        );
        capture(sentryError, {
          tags: {
            errorSource: "tanstack-query",
            statusCode,
            queryKey: JSON.stringify(event?.query?.queryKey),
          },
          extra: {
            timestamp: new Date().toISOString(),
            queryInfo: {
              queryKey: event?.query?.queryKey,
              queryHash: event?.query?.queryHash,
            },
            error: {
              name: safeError.name,
              message: safeError.message,
              status: statusCode,
            },
          },
        });
      }
    }

    if (
      event?.type === "updated" &&
      (event as { action?: { type?: string; error?: unknown } })?.action
        ?.type === "error"
    ) {
      const action = (
        event as {
          action: {
            error?: {
              response?: {
                errors?: Array<{
                  message?: string;
                  path?: string[];
                  extensions?: Record<string, unknown>;
                }>;
              };
              stack?: string;
            };
          };
        }
      ).action;
      const gqlError = getFirstGraphQlErrorFromAction(action);
      if (gqlError) {
        if (
          shouldIgnoreGraphQlQueryMessage(
            gqlError.message,
            ignoredGraphQlQueryMessagesSet,
          )
        ) {
          return;
        }
        const sentryError = new Error(toSentryErrorMessage(gqlError.message));
        // Not `action.error.stack` as-is: a ClientError's stack starts with
        // its `.message`, which embeds the full request (variables
        // included), so swap that header for the safe message.
        const stack = resolveGraphQlSentryStack(
          toSafeStack(action.error, sentryError.message),
          gqlError,
        );
        if (stack) {
          sentryError.stack = stack;
        }
        const statusCode = graphQlSentryStatusCode(gqlError);
        capture(sentryError, {
          tags: {
            errorSource: "tanstack-query",
            graphqlPath: graphQlPathTag(gqlError.path),
            statusCode,
            errorCode: graphQlSentryErrorCode(gqlError),
          },
          extra: {
            timestamp: new Date().toISOString(),
            queryInfo: {
              queryKey: event?.query?.queryKey,
              queryHash: event?.query?.queryHash,
            },
            error: gqlError,
          },
        });
      }
    }
  });

  // Mutation cache: 5xx, network, 400/422
  queryClient
    .getMutationCache()
    .subscribe((event: MutationCacheNotifyEvent) => {
      const mutation = event?.mutation;
      if (!mutation || mutation.state.status !== "error") return;
      const error = mutation.state.error;
      if (!shouldReportMutationError(error)) return;

      const err = error as ErrorWithStatusCode;
      const statusCode = err.statusCode;

      // graphql-request's ClientError bakes the full request — including
      // mutation *variables* like a signUp password — into its own
      // `.message` via `JSON.stringify({ response, request })`. Forwarding
      // that error as-is would leak those variables to Sentry (this is what
      // happened in CRM-NEXTJS-B9), since Sentry reads `.message` directly
      // into the reported exception. When the error looks like a GraphQL
      // ClientError, report a clean error built from just the GraphQL
      // message instead of the raw error.
      const gqlError = getGraphQlErrorFromUnknown(error);
      const operationName = graphQlOperationName(error);
      let sentryError: Error;
      if (gqlError) {
        sentryError = new Error(toSentryErrorMessage(gqlError.message));
        const stack = resolveGraphQlSentryStack(
          toSafeStack(error, sentryError.message),
          gqlError,
        );
        if (stack) {
          sentryError.stack = stack;
        }
        // Keep the original error's name (graphql-request's ClientError
        // never sets one, so it's "Error", same as main reported) and the
        // duck-typed shape (`request.query` + `response.errors`) that
        // gqlClientErrorFingerprint reads from `hint.originalException`, so
        // mutations still group by operation/code — but carry only the query
        // text, never `variables`.
        const query = (error as { request?: { query?: unknown } }).request
          ?.query;
        sentryError.name = error instanceof Error ? error.name : "Error";
        Object.assign(sentryError, {
          request: { query: typeof query === "string" ? query : "" },
          response: { errors: [gqlError] },
        });
      } else {
        // No GraphQL `errors` (e.g. an HTTP 5xx / HTML error page) — but a
        // ClientError's message still embeds the full request, so strip the
        // serialized payload rather than forwarding the raw error.
        const safeError = toSafeReportedError(error);
        sentryError = new Error(
          toSentryErrorMessage(safeError.message, "Error"),
        );
        sentryError.name = safeError.name;
        const stack = toSafeStack(error, sentryError.message);
        if (stack) {
          sentryError.stack = stack;
        }
      }

      capture(sentryError, {
        tags: {
          errorSource: "tanstack-query-mutation",
          ...(statusCode != null && { statusCode: String(statusCode) }),
          ...(gqlError && { graphqlPath: graphQlPathTag(gqlError.path) }),
          ...(gqlError && { errorCode: graphQlSentryErrorCode(gqlError) }),
          ...(operationName && { graphqlOperation: operationName }),
          mutationKey: JSON.stringify(mutation.options.mutationKey ?? []),
        },
        extra: {
          timestamp: new Date().toISOString(),
          mutationKey: mutation.options.mutationKey,
          statusCode,
        },
      });
    });

  return queryClient;
}
